// pages/MockTestPage.js
// ─────────────────────────────────────────────────────────────
// Student opens the WhatsApp link → lands on this page.
// URL: /mocktest?s=SESSION_ID
//
// Flow:
//  1. Read ?s= from URL
//  2. GET /getTestSession  → get grade/subject/chapter/numQ/timeLimit
//  3. GET /dynamiconeword  → fetch questions
//  4. Student enters name → test starts with timer
//  5. Submit → POST /submitTestResult → navigate to /mocktestresult
// ─────────────────────────────────────────────────────────────
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { BASE_API_URL, API_HEADERS } from "./config";

// ── Inline styles ─────────────────────────────────────────────
var S = {
  page: {
    minHeight: "100vh",
    background: "#f0f4f8",
    fontFamily: "'Segoe UI', sans-serif",
    padding: "0 0 40px 0",
  },
  topBar: {
    background: "#1a237e",
    color: "#fff",
    padding: "14px 20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  topTitle: { fontSize: "16px", fontWeight: "700" },
  timer: {
    fontSize: "15px",
    fontWeight: "700",
    background: "#fff",
    color: "#c62828",
    padding: "4px 12px",
    borderRadius: "20px",
  },
  progress: {
    height: "5px",
    background: "#e0e0e0",
  },
  progressBar: {
    height: "5px",
    background: "#43a047",
    transition: "width 0.3s",
  },
  center: {
    display: "flex",
    justifyContent: "center",
    padding: "32px 16px",
  },
  card: {
    background: "#fff",
    borderRadius: "14px",
    boxShadow: "0 2px 16px rgba(0,0,0,0.09)",
    padding: "28px 24px",
    width: "100%",
    maxWidth: "600px",
  },
  qNum: { fontSize: "12px", color: "#888", marginBottom: "8px" },
  qText: {
    fontSize: "17px",
    fontWeight: "600",
    color: "#1a1a2e",
    marginBottom: "20px",
    lineHeight: "1.5",
  },
  optionBtn: function(selected, correct, wrong, revealed) {
    var bg = "#f5f6fa";
    var border = "1.5px solid #ddd";
    var color = "#333";
    if (selected && !revealed) { bg = "#e8eaf6"; border = "2px solid #3949ab"; color = "#1a237e"; }
    if (revealed && correct)   { bg = "#e8f5e9"; border = "2px solid #43a047"; color = "#2e7d32"; }
    if (revealed && wrong)     { bg = "#ffebee"; border = "2px solid #e53935"; color = "#c62828"; }
    return {
      width: "100%",
      textAlign: "left",
      padding: "12px 16px",
      marginBottom: "10px",
      borderRadius: "10px",
      background: bg,
      border: border,
      color: color,
      fontSize: "15px",
      cursor: revealed ? "default" : "pointer",
      fontWeight: selected ? "600" : "400",
      transition: "all 0.15s",
    };
  },
  navRow: {
    display: "flex",
    gap: "12px",
    marginTop: "20px",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },
  btnPrev: {
    padding: "10px 20px",
    borderRadius: "8px",
    border: "1.5px solid #ccc",
    background: "#fff",
    color: "#555",
    fontWeight: "600",
    cursor: "pointer",
    fontSize: "14px",
  },
  btnNext: {
    padding: "10px 22px",
    borderRadius: "8px",
    border: "none",
    background: "#1a237e",
    color: "#fff",
    fontWeight: "700",
    cursor: "pointer",
    fontSize: "14px",
  },
  btnSubmit: {
    padding: "12px 28px",
    borderRadius: "8px",
    border: "none",
    background: "#43a047",
    color: "#fff",
    fontWeight: "700",
    cursor: "pointer",
    fontSize: "15px",
  },
  paletteDot: function(status) {
    var bg = "#e0e0e0";
    if (status === "answered")   bg = "#43a047";
    if (status === "current")    bg = "#1a237e";
    return {
      width: "28px",
      height: "28px",
      borderRadius: "50%",
      background: bg,
      color: bg === "#e0e0e0" ? "#555" : "#fff",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "11px",
      fontWeight: "600",
      margin: "3px",
      cursor: "pointer",
      border: status === "current" ? "2px solid #3949ab" : "none",
    };
  },
  namePage: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f0f4f8",
    padding: "24px",
  },
  nameCard: {
    background: "#fff",
    borderRadius: "16px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
    padding: "36px 28px",
    maxWidth: "420px",
    width: "100%",
    textAlign: "center",
    fontFamily: "'Segoe UI', sans-serif",
  },
  msgPage: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f0f4f8",
    fontFamily: "'Segoe UI', sans-serif",
    textAlign: "center",
    padding: "24px",
  },
};

// ── Shuffle array ─────────────────────────────────────────────
function shuffleArray(arr) {
  var a = arr.slice();
  for (var i = a.length - 1; i > 0; i--) {
    var j = Math.floor(Math.random() * (i + 1));
    var tmp = a[i]; a[i] = a[j]; a[j] = tmp;
  }
  return a;
}

// ── Format time MM:SS ─────────────────────────────────────────
function formatTime(secs) {
  var m = Math.floor(secs / 60);
  var s = secs % 60;
  return (m < 10 ? "0" : "") + m + ":" + (s < 10 ? "0" : "") + s;
}

// ─────────────────────────────────────────────────────────────
export default function MockTestPage() {
  var navigate = useNavigate();

  // ── State: session loading ────────────────────────────────
  var [session,    setSession]    = useState(null);
  var [questions,  setQuestions]  = useState([]);
  var [loadError,  setLoadError]  = useState("");
  var [pageReady,  setPageReady]  = useState(false); // all loaded

  // ── State: student name entry ─────────────────────────────
  var [phase,      setPhase]      = useState("loading"); // loading | name | test | submitting
  var [studentName, setStudentName] = useState("");

  // ── State: test ───────────────────────────────────────────
  var [current,    setCurrent]    = useState(0);
  var [answers,    setAnswers]    = useState({});   // { qIndex: "A"/"B"/"C"/"D" }
  var [timeLeft,   setTimeLeft]   = useState(0);
  var [startTime,  setStartTime]  = useState(null);
  var timerRef = useRef(null);

  // ── 1. Read session_id from URL ───────────────────────────
  var sessionID = new URLSearchParams(window.location.search).get("s");

  // ── 2. Load session + questions on mount ──────────────────
  useEffect(function() {
    if (!sessionID) {
      setLoadError("No test session found in the link. Please ask your teacher for a new link.");
      return;
    }

    async function load() {
      // Step A: get session
      try {
        var res = await fetch(BASE_API_URL + "/getTestSession?s=" + sessionID);
        if (res.status === 410) { setLoadError("This test link has expired. Please ask your teacher for a new one."); return; }
        if (res.status === 409) { setLoadError("This test has already been submitted. You cannot retake it."); return; }
        if (!res.ok) { setLoadError("Could not load test. Please try again."); return; }
        var sess = await res.json();
        setSession(sess);

        // Step B: fetch questions from existing /dynamiconeword
        var qUrl = BASE_API_URL + "/dynamiconeword?grade_level=" + encodeURIComponent(sess.grade_level);
        if (sess.subject) qUrl += "&subject=" + encodeURIComponent(sess.subject);
        if (sess.chapter) qUrl += "&chapter=" + encodeURIComponent(sess.chapter);
        if (sess.unit)    qUrl += "&unit="    + encodeURIComponent(sess.unit);
        if (sess.medium)  qUrl += "&medium="  + encodeURIComponent(sess.medium);

        var qRes = await fetch(qUrl, { headers: API_HEADERS });
        if (!qRes.ok) { setLoadError("Could not load questions. Please try again."); return; }
        var allQ = await qRes.json();

        if (!allQ || allQ.length === 0) {
          setLoadError("No questions found for this test configuration. Please contact your teacher.");
          return;
        }

        // Shuffle and trim to num_questions
        var picked = shuffleArray(allQ).slice(0, sess.num_questions);
        setQuestions(picked);
        setTimeLeft(sess.time_limit_mins * 60);
        setPageReady(true);
        setPhase("name");

      } catch (err) {
        setLoadError("Network error: " + err.message);
      }
    }

    load();
  }, [sessionID]);

  // ── 3. Timer (starts when phase = "test") ─────────────────
  useEffect(function() {
    if (phase !== "test") return;

    setStartTime(Date.now());
    timerRef.current = setInterval(function() {
      setTimeLeft(function(prev) {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return function() { clearInterval(timerRef.current); };
  }, [phase]);

  // ── 4. Start test after name entry ────────────────────────
  function handleStartTest(e) {
    e.preventDefault();
    if (!studentName.trim()) return;
    setPhase("test");
  }

  // ── 5. Answer selection ───────────────────────────────────
  function selectAnswer(opt) {
    if (answers[current]) return; // no change after answering
    setAnswers(function(prev) {
      return Object.assign({}, prev, { [current]: opt });
    });
  }

  // ── 6. Navigation ─────────────────────────────────────────
  function goNext() { if (current < questions.length - 1) setCurrent(function(c) { return c + 1; }); }
  function goPrev() { if (current > 0) setCurrent(function(c) { return c - 1; }); }

  // ── 7. Build result and submit ────────────────────────────
  function buildAndSubmit() {
    clearInterval(timerRef.current);
    setPhase("submitting");

    var correct = 0, wrong = 0, unattempted = 0;
    var answersArr = questions.map(function(q, i) {
      var selected = answers[i] || null;
      var isCorrect = selected && selected.toUpperCase() === q.correct_option.toUpperCase();
      if (!selected)   unattempted++;
      else if (isCorrect) correct++;
      else              wrong++;
      return {
        question_id:     q.id,
        question_text:   q.question_text,
        selected_option: selected,
        correct_option:  q.correct_option,
        is_correct:      isCorrect,
        subject:         q.subject,
        chapter:         q.chapter,
      };
    });

    var totalSecs = session ? session.time_limit_mins * 60 - timeLeft : 0;
    if (startTime) totalSecs = Math.round((Date.now() - startTime) / 1000);

    var scorePct = questions.length > 0
      ? parseFloat(((correct / questions.length) * 100).toFixed(2))
      : 0;

    var payload = {
      session_id:      sessionID,
      student_name:    studentName.trim(),
      total_questions: questions.length,
      correct_answers: correct,
      wrong_answers:   wrong,
      unattempted:     unattempted,
      score_percentage: scorePct,
      time_taken_secs: totalSecs,
      answers_json:    JSON.stringify(answersArr),
      pdf_base64:      "",   // no PDF — teacher gets score summary by email
      pdf_filename:    "",
    };

    fetch(BASE_API_URL + "/submitTestResult", {
      method:  "POST",
      headers: API_HEADERS,
      body:    JSON.stringify(payload),
    })
      .then(function(res) { return res.json(); })
      .then(function(data) {
        // Navigate to result page with all data
        navigate("/mocktestresult", {
          state: {
            studentName:    studentName.trim(),
            correct:        correct,
            wrong:          wrong,
            unattempted:    unattempted,
            total:          questions.length,
            scorePct:       scorePct,
            timeTakenSecs:  totalSecs,
            teacherWaLink:  data.teacher_wa_link || "",
            answersArr:     answersArr,
            subject:        session ? session.subject : "",
            chapter:        session ? session.chapter : "",
          },
        });
      })
      .catch(function(err) {
        alert("Submit failed: " + err.message + ". Please try again.");
        setPhase("test");
      });
  }

  function handleAutoSubmit() {
    if (phase === "test") buildAndSubmit();
  }

  function handleManualSubmit() {
    var answered = Object.keys(answers).length;
    var unattempted = questions.length - answered;
    var msg = unattempted > 0
      ? "You have " + unattempted + " unanswered question(s). Submit anyway?"
      : "Submit the test now?";
    if (window.confirm(msg)) buildAndSubmit();
  }

  // ─────────────────────────────────────────────────────────
  // ── Render: Loading ──────────────────────────────────────
  if (phase === "loading" && !loadError) {
    return <div style={S.msgPage}><div><div style={{fontSize:"32px"}}>⏳</div><p>Loading your test...</p></div></div>;
  }

  // ── Render: Error ────────────────────────────────────────
  if (loadError) {
    return (
      <div style={S.msgPage}>
        <div style={{background:"#fff",padding:"32px",borderRadius:"14px",maxWidth:"400px",boxShadow:"0 2px 16px rgba(0,0,0,0.09)"}}>
          <div style={{fontSize:"36px",marginBottom:"12px"}}>⚠️</div>
          <div style={{fontSize:"16px",fontWeight:"600",color:"#c62828",marginBottom:"8px"}}>Test Unavailable</div>
          <div style={{fontSize:"14px",color:"#555"}}>{loadError}</div>
        </div>
      </div>
    );
  }

  // ── Render: Name Entry ────────────────────────────────────
  if (phase === "name") {
    var sess = session || {};
    return (
      <div style={S.namePage}>
        <div style={S.nameCard}>
          <div style={{fontSize:"36px",marginBottom:"8px"}}>📝</div>
          <div style={{fontSize:"20px",fontWeight:"700",color:"#1a237e",marginBottom:"4px"}}>VirtualKalvi Mock Test</div>
          <div style={{fontSize:"13px",color:"#888",marginBottom:"20px"}}>
            {sess.subject} {sess.chapter ? "| " + sess.chapter : ""} | {sess.num_questions} Questions | {sess.time_limit_mins} min | {sess.medium}
          </div>
          <form onSubmit={handleStartTest}>
            <input
              style={{
                width:"100%", padding:"12px 14px", borderRadius:"10px",
                border:"1.5px solid #ddd", fontSize:"15px",
                marginBottom:"16px", boxSizing:"border-box",
              }}
              type="text"
              placeholder="Enter your full name to begin"
              value={studentName}
              onChange={function(e) { setStudentName(e.target.value); }}
              required
              autoFocus
            />
            <button
              style={{
                width:"100%", padding:"13px", borderRadius:"10px",
                border:"none", background:"#1a237e", color:"#fff",
                fontSize:"16px", fontWeight:"700", cursor:"pointer",
              }}
              type="submit"
            >
              🚀 Start Test
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Render: Submitting ────────────────────────────────────
  if (phase === "submitting") {
    return <div style={S.msgPage}><div><div style={{fontSize:"32px"}}>📤</div><p>Submitting your test...</p></div></div>;
  }

  // ── Render: Test ──────────────────────────────────────────
  var q = questions[current];
  if (!q) return null;

  var selectedOpt    = answers[current] || null;
  var progressPct    = ((current + 1) / questions.length) * 100;
  var answeredCount  = Object.keys(answers).length;

  var options = [
    { key: "A", text: q.option_a },
    { key: "B", text: q.option_b },
    { key: "C", text: q.option_c },
    { key: "D", text: q.option_d },
  ];

  return (
    <div style={S.page}>

      {/* ── Top bar ── */}
      <div style={S.topBar}>
        <div style={S.topTitle}>VK Mock Test — {studentName}</div>
        <div style={S.timer}>{formatTime(timeLeft)}</div>
      </div>

      {/* ── Progress ── */}
      <div style={S.progress}>
        <div style={Object.assign({}, S.progressBar, { width: progressPct + "%" })} />
      </div>

      {/* ── Question Card ── */}
      <div style={S.center}>
        <div style={S.card}>

          <div style={S.qNum}>
            Question {current + 1} of {questions.length}
            {q.subject ? "  ·  " + q.subject : ""}
            {q.chapter ? "  ·  " + q.chapter : ""}
          </div>

          <div style={S.qText}>{q.question_text}</div>

          {/* ── Options ── */}
          {options.map(function(opt) {
            var isSelected = selectedOpt === opt.key;
            return (
              <button
                key={opt.key}
                style={S.optionBtn(isSelected, false, false, false)}
                onClick={function() { selectAnswer(opt.key); }}
                disabled={!!selectedOpt}
              >
                <span style={{fontWeight:"700",marginRight:"10px"}}>{opt.key}.</span>
                {opt.text}
              </button>
            );
          })}

          {/* ── Navigation ── */}
          <div style={S.navRow}>
            <button style={S.btnPrev} onClick={goPrev} disabled={current === 0}>← Prev</button>

            <span style={{fontSize:"12px",color:"#888",alignSelf:"center"}}>
              {answeredCount}/{questions.length} answered
            </span>

            {current < questions.length - 1
              ? <button style={S.btnNext} onClick={goNext}>Next →</button>
              : <button style={S.btnSubmit} onClick={handleManualSubmit}>✅ Submit Test</button>
            }
          </div>

          {/* ── Question Palette ── */}
          <div style={{marginTop:"20px",borderTop:"1px solid #eee",paddingTop:"14px"}}>
            <div style={{fontSize:"12px",color:"#888",marginBottom:"8px"}}>Question palette (tap to jump)</div>
            <div>
              {questions.map(function(_, i) {
                var status = i === current ? "current" : (answers[i] ? "answered" : "empty");
                return (
                  <span
                    key={i}
                    style={S.paletteDot(status)}
                    onClick={function() { setCurrent(i); }}
                  >
                    {i + 1}
                  </span>
                );
              })}
            </div>
          </div>

          {/* ── Submit button (visible on last question) ── */}
          {current === questions.length - 1 && (
            <button
              style={Object.assign({}, S.btnSubmit, {width:"100%",marginTop:"16px"})}
              onClick={handleManualSubmit}
            >
              ✅ Submit Test
            </button>
          )}

        </div>
      </div>
    </div>
  );
}
